import { createQueryFn } from '@oneview/redux/utils';
import { LegalDocumentClassifications, PolicyOnAccount, PolicyValuesType } from '@orbis-systems/orbis-api-middleware';
import { showToast } from '@orbis-systems/orbis-react-toolkit';
import FileSaver from 'file-saver';
import { api } from './baseApi';

export const documentApi = api.injectEndpoints({
    endpoints: (build) => ({
        getAccountDocuments: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        accountNumber,
                        docType,
                        fromDate,
                        toDate,
                    }: { accountNumber: string; docType: string; fromDate?: Date; toDate?: Date },
                    { extra: api },
                ) => api.orbisMiddleware.document.getAccountDocuments(accountNumber, docType, fromDate, toDate),
            ),
        }),
        getAccountDocumentsConfirms: build.query({
            queryFn: createQueryFn(
                async (
                    { accountNumber, fromDate, toDate }: { accountNumber: string; fromDate?: Date; toDate?: Date },
                    { extra: api },
                ) => api.orbisMiddleware.document.getAccountDocumentsConfirms(accountNumber, fromDate, toDate),
            ),
        }),
        downloadDocument: build.mutation({
            queryFn: createQueryFn(
                async ({ accountNumber, key }: { accountNumber: string; key: string }, { extra: api }) => {
                    const data = await api.orbisMiddleware.document.downloadDocument(accountNumber, key);
                    FileSaver.saveAs(data, `${key}.pdf`);
                    return data;
                },
            ),
        }),
        getPolicyValues: build.query({
            queryFn: createQueryFn(async (type: PolicyValuesType, { extra: api }) =>
                api.orbisMiddleware.document.getPolicyValues(type),
            ),
        }),
        updatePolicyOnAccount: build.mutation({
            queryFn: createQueryFn(
                async (
                    { accountId, policy, type }: { accountId: number; policy: PolicyOnAccount; type: PolicyValuesType },
                    { extra: api },
                ) => api.orbisMiddleware.document.updatePolicyOnAccount(accountId, policy, type),
            ),
        }),
        getLegalDocuments: build.query({
            queryFn: createQueryFn(async (classification: LegalDocumentClassifications, { extra: api }) =>
                api.orbisMiddleware.document.getLegalDocuments(classification),
            ),
        }),
        getLegalDocumentContent: build.query({
            queryFn: createQueryFn(async (id: string | number, { extra: api }) =>
                api.orbisMiddleware.document.getLegalDocumentContent(id),
            ),
        }),
        getFundingAgreements: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) =>
                api.orbisMiddleware.document.getFundingAgreements(),
            ),
        }),
        downloadLegalDocument: build.mutation({
            queryFn: createQueryFn(async (id: string, { extra: api }) => {
                const data = await api.orbisMiddleware.document.downloadLegalDocument(id);
                FileSaver.saveAs(data, `${id}.pdf`);
                return data;
            }),
        }),
        signLegalDocument: build.mutation({
            queryFn: createQueryFn(
                async (
                    params: {
                        esigned: boolean;
                        documentId: number | string;
                        signature: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    // @ts-expect-error: need to add url to type
                    const data = await api.orbisMiddleware.documents.signLegalDocument(params);
                    dispatch(showToast('Document signed'));
                    return data;
                },
            ),
        }),
    }),
});

export const {
    useGetAccountDocumentsQuery,
    useGetAccountDocumentsConfirmsQuery,
    useGetPolicyValuesQuery,
    useUpdatePolicyOnAccountMutation,
    useLazyGetLegalDocumentsQuery,
    useLazyGetLegalDocumentContentQuery,
    useGetFundingAgreementsQuery,
    useDownloadDocumentMutation,
    useDownloadLegalDocumentMutation,
    useSignLegalDocumentMutation,
} = documentApi;
